import { useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AppShell } from '@/components/layout/app-shell';
import { TooltipProvider } from '@/components/ui/tooltip';
import { getAuthPort } from '@/data/auth';
import { AgendamentosPage } from '@/features/agendamentos/AgendamentosPage';
import { AgendarPage } from '@/features/agendar/AgendarPage';
import { ContratosPage } from '@/features/contratos/ContratosPage';
import { HistoricoPage } from '@/features/historico/HistoricoPage';
import { HomePage } from '@/features/home/HomePage';
import { NovidadesPage } from '@/features/novidades/NovidadesPage';
import { VersaoSaudePage } from '@/features/versao-saude/VersaoSaudePage';

/**
 * Raiz da aplicação.
 *
 * Comparada ao app do monorepo, aqui não há `NasajonAppShell`, `useMenuConfig`,
 * `TemplateShellRoute`, `DynamicPage` nem `registerAllApis`: as rotas apontam
 * para componentes React reais, não para identificadores de layout resolvidos
 * em runtime contra o ERPrompt-API. O ganho prático é que uma rota quebrada
 * falha em build, não em produção na frente do cliente.
 */
export default function App() {
  const [usuario, setUsuario] = useState<{ nome: string; detalhe: string } | null>(null);
  const [roles, setRoles] = useState<string[]>([]);

  useEffect(() => {
    let ativo = true;
    void getAuthPort()
      .getIdentity()
      .then((identidade) => {
        if (ativo) setUsuario(identidade);
      })
      .catch(() => {
        // Identidade é enfeite da topbar: falhar aqui não pode derrubar o app.
        if (ativo) setUsuario(null);
      });
    void getAuthPort()
      .getRoles()
      .then((valor) => {
        if (ativo) setRoles(valor);
      })
      .catch(() => {
        if (ativo) setRoles([]);
      });
    return () => {
      ativo = false;
    };
  }, []);

  return (
    <TooltipProvider>
      <AppShell usuario={usuario} roles={roles}>
        <Routes>
          <Route path="/" element={<Navigate to="/home" replace />} />
          <Route path="/home" element={<HomePage />} />
          <Route path="/versao-saude" element={<VersaoSaudePage />} />
          <Route path="/contratos" element={<ContratosPage />} />
          <Route path="/agendar" element={<AgendarPage />} />
          <Route path="/agendamentos" element={<AgendamentosPage roles={roles} />} />
          <Route path="/historico" element={<HistoricoPage />} />
          <Route path="/novidades" element={<NovidadesPage roles={roles} />} />
          {/* "Administração" deixou de ser página — agora é modal, aberto
              pelo dropdown do usuário (`UserChip`/`AdministracaoDialog`). */}
          <Route path="*" element={<Navigate to="/home" replace />} />
        </Routes>
      </AppShell>

      {/* Toast pontual: confirma ação discreta; erro que exige decisão vira
          estado inline na própria tela, não um toast que some sozinho. */}
      <Toaster position="top-right" richColors closeButton toastOptions={{ duration: 5000 }} />
    </TooltipProvider>
  );
}
